import { useState, useCallback, useMemo, ReactNode, useEffect } from 'react'
import { WalletContext, WalletContextType } from '@/contexts/WalletContext'
import { ConnectionStatus } from '@/constants/connectionStatus'
import { useNotification } from '@/hooks/useNotification'
import { getEnv } from '@/config/env'
import { formatAddress } from '@/utils/formatting'
import { usePublicClient, useChainId } from 'wagmi'
import type { Abi } from 'viem'
import SafeAbi from '@/abis/Safe.json'
import { getDemoWalletAddress } from '@/utils/demoWallet'

interface WalletProviderProps {
  children: ReactNode
}

export default function WalletProvider({ children }: WalletProviderProps) {
  const [status, setStatus] = useState<ConnectionStatus>(
    ConnectionStatus.Disconnected
  )
  const [address, setAddress] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const [isCheckingAdmin, setIsCheckingAdmin] = useState<boolean>(false)
  const [treasuryOwners, setTreasuryOwners] = useState<string[]>([])
  const [areTreasuryOwnersResolved, setAreTreasuryOwnersResolved] =
    useState<boolean>(false)
  const { showError } = useNotification()
  const { treasurySafe } = useMemo(() => getEnv(), [])
  const demoWalletAddress = useMemo(() => getDemoWalletAddress(), [])
  const publicClient = usePublicClient()
  const chainId = useChainId()

  const connectWallet = useCallback(async () => {
    if (status === ConnectionStatus.Connected) {
      return
    }

    if (demoWalletAddress) {
      setAddress(demoWalletAddress)
      setIsCheckingAdmin(true)
      setStatus(ConnectionStatus.Connected)
      return
    }

    if (typeof window.ethereum === 'undefined') {
      showError('MetaMask is not installed. Please install MetaMask extension.')
      return
    }

    setStatus(ConnectionStatus.Connecting)

    try {
      const accounts = (await window.ethereum.request({
        method: 'eth_requestAccounts',
      })) as string[]

      if (accounts && accounts.length > 0) {
        const userAddress = accounts[0]
        setAddress(userAddress)
        setIsCheckingAdmin(true)
        setStatus(ConnectionStatus.Connected)
      } else {
        setStatus(ConnectionStatus.Disconnected)
        setIsCheckingAdmin(false)
        showError(
          'No accounts found. Please make sure you are logged in to MetaMask.'
        )
      }
    } catch (error) {
      console.error('Error connecting wallet:', error)
      setStatus(ConnectionStatus.Disconnected)
      setIsCheckingAdmin(false)
      showError('Failed to connect to MetaMask.')
    }
  }, [status, showError, demoWalletAddress])

  useEffect(() => {
    if (!demoWalletAddress || status === ConnectionStatus.Connected) {
      return
    }

    setAddress(demoWalletAddress)
    setIsCheckingAdmin(true)
    setStatus(ConnectionStatus.Connected)
  }, [demoWalletAddress, status])

  useEffect(() => {
    const fetchTreasuryOwners = async () => {
      setAreTreasuryOwnersResolved(false)

      if (!publicClient || !treasurySafe || !chainId) {
        setTreasuryOwners([])
        return
      }

      try {
        const owners = (await publicClient.readContract({
          address: treasurySafe as `0x${string}`,
          abi: SafeAbi as Abi,
          functionName: 'getOwners',
        })) as `0x${string}`[]

        const ownersLower = owners.map((owner) => owner.toLowerCase())
        setTreasuryOwners(ownersLower)
      } catch (error) {
        console.error('Error fetching treasury owners:', error)
        setTreasuryOwners([])
      } finally {
        setAreTreasuryOwnersResolved(true)
      }
    }

    fetchTreasuryOwners()
  }, [publicClient, treasurySafe, chainId])

  useEffect(() => {
    const checkAccess = () => {
      if (status !== ConnectionStatus.Connected || !address) {
        setIsAdmin(false)
        setIsCheckingAdmin(false)
        return
      }

      if (!areTreasuryOwnersResolved) {
        setIsCheckingAdmin(true)
        return
      }

      setIsCheckingAdmin(true)

      try {
        const userAddressLower = address.toLowerCase()
        const isAdminUser = treasuryOwners.includes(userAddressLower)
        setIsAdmin(isAdminUser)
      } catch (error) {
        console.error('Error checking access:', error)
        setIsAdmin(false)
      } finally {
        setIsCheckingAdmin(false)
      }
    }

    checkAccess()
  }, [status, address, treasuryOwners, areTreasuryOwnersResolved])

  const disconnectWallet = useCallback(() => {
    setAddress('')
    setIsAdmin(false)
    setIsCheckingAdmin(false)
    setStatus(ConnectionStatus.Disconnected)
  }, [])

  useEffect(() => {
    if (demoWalletAddress) {
      return
    }

    if (typeof window.ethereum === 'undefined') {
      return
    }

    const handleAccountsChanged = (accounts: unknown) => {
      const accountList = accounts as string[]
      if (!accountList || accountList.length === 0) {
        disconnectWallet()
      } else if (status === ConnectionStatus.Connected) {
        const newAddress = accountList[0]
        if (newAddress && newAddress.toLowerCase() !== address.toLowerCase()) {
          setAddress(newAddress)
          setIsCheckingAdmin(true)
        }
      }
    }

    const ethereum = window.ethereum
    if (ethereum && ethereum.on) {
      ethereum.on('accountsChanged', handleAccountsChanged)
    }

    return () => {
      if (ethereum && ethereum.removeListener) {
        ethereum.removeListener('accountsChanged', handleAccountsChanged)
      }
    }
  }, [status, address, disconnectWallet, demoWalletAddress])

  const contextValue = useMemo<WalletContextType>(
    () => ({
      status,
      address,
      isAdmin,
      isCheckingAdmin,
      connectWallet,
      disconnectWallet,
      formatAddress,
    }),
    [status, address, isAdmin, isCheckingAdmin, connectWallet, disconnectWallet]
  )

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  )
}
